import React, { memo, useEffect } from 'react';
import { ComparisonListStatus } from '@navi-toolbox/ui-components';
import { useSnackbar } from 'fronton-react';

import { useGetText } from './hooks/use-get-text';
import { useGetButtonText } from './hooks/use-get-button-text';
import { useCompareActions } from './hooks/use-compare-actions';
import { useCompareStatusState } from './hooks/use-compare-status-state';
import { AUTO_HIDE_TIMER_MS, COMPARE_STATUS_ID } from './constants';
import { useGetHeader } from './hooks/use-get-header';
import { RESET_PRODUCTS_COMPARE_ACTION, RESET_VALIDATION } from 'features/products-compare/ducks/actions';
import { useDispatch, useSelector } from 'react-redux';
import { getValidationError } from 'features/products-compare/ducks/selectors/validation';

type Props = {
  skipDataLoading?: boolean;
};

export const CompareStatus = memo(({ skipDataLoading = false }: Props) => {
  const validationError = useSelector(getValidationError);
  const { operation, mode, isError, shouldShow, redirectTo } = useCompareStatusState(validationError);
  const { handleButtonClick } = useCompareActions({ skipDataLoading });
  const text = useGetText({
    operationType: operation,
    status: isError ? 'error' : 'success',
  });
  const buttonText = useGetButtonText({ mode });
  const header = useGetHeader();
  const dispatch = useDispatch();

  useEffect(() => {
    if (shouldShow && mode === 'alert' && validationError) {
      const timer = setTimeout(() => {
        dispatch(RESET_PRODUCTS_COMPARE_ACTION());
        dispatch(RESET_VALIDATION());
      }, AUTO_HIDE_TIMER_MS);
      return () => clearTimeout(timer);
    }
  }, [shouldShow, mode, validationError]);

  const { state, addSnack, removeSnack } = useSnackbar();

  useEffect(() => {
    if (shouldShow && text) {
      addSnack({
        id: COMPARE_STATUS_ID,
        mode,
        header,
        redirectTo,
        text,
        buttonText,
        onClose: () => {
          removeSnack(COMPARE_STATUS_ID);
        },
        onClick: () => {
          handleButtonClick(mode);
          removeSnack(COMPARE_STATUS_ID);
        },
      });
    }
    return () => {
      removeSnack(COMPARE_STATUS_ID);
    };
  }, [addSnack, buttonText, handleButtonClick, header, mode, redirectTo, removeSnack, shouldShow, text]);

  if (!shouldShow || !text) {
    return null;
  }

  return <ComparisonListStatus autoHideTimer={AUTO_HIDE_TIMER_MS} items={state} />;
});
